import { yupResolver } from '@hookform/resolvers/yup';
import { AllergyMasterAddPayload } from 'models';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LiaTimesSolid } from 'react-icons/lia';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { ALLERGY_MASTER_TYPE_SELECTSOURCE } from '../constants';
import useAllergyMaster from '../hooks/useAllergyMaster';

interface AllergyMasterFormProps {
  nextDisplayOrder: number;
  onClose: () => void;
  onReflect?: () => void;
}

function AllergyMasterForm({ nextDisplayOrder, onClose, onReflect }: AllergyMasterFormProps) {
  const { t } = useTranslation();

  const { addAllergyMaster } = useAllergyMaster();

  // schema validation
  const schema = yup
    .object({
      type: yup.number().required(),
      allergy_name: yup.string().required(t('AllergyMaster.warn_field_required')),
      display_order: yup
        .number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && originalValue.trim() === '') {
            return undefined;
          }
          return value;
        })
        .min(1, t('AllergyMaster.warn_number_required'))
        .integer(t('AllergyMaster.warn_number_required'))
        .required(t('AllergyMaster.warn_number_required')),
      display_flg: yup.boolean().required(),
    })
    .required();

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<AllergyMasterAddPayload>({
    resolver: yupResolver(schema),
  });
  const onSubmit = async (payload: AllergyMasterAddPayload) => {
    const { data, error } = await addAllergyMaster(payload);
    if (data) {
      toast.success(data?.message);
      onReflect?.();
      onClose();
    } else {
      if (error && error.error) {
        const values = getValues();
        const fieldNames = Object.keys(values);

        toast.error(<div dangerouslySetInnerHTML={{ __html: error.message || t('App.unexpected_error') }}></div>);
        for (const key of Object.keys(error.error)) {
          if (fieldNames.includes(key)) {
            setError(key as keyof AllergyMasterAddPayload, { type: 'custom', message: error.error[key] });
          }
        }
      }
    }
  };

  return (
    <Modal
      show
      size="lg"
      dialogClassName="allergy-master-form-modal"
      backdrop="static"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={onClose}
    >
      <Modal.Header className="border-bottom-0 justify-content-end">
        <Button variant="primary" className="btn--auto" onClick={onClose}>
          <LiaTimesSolid size={16} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Form.Group as={Row} className="mb-3" controlId="type">
            <Form.Label column sm="3" className="text-end">
              {t('AllergyMaster.type')}
            </Form.Label>
            <Col sm="9">
              <Form.Select {...register('type')} autoFocus>
                {ALLERGY_MASTER_TYPE_SELECTSOURCE.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="allergy_name">
            <Form.Label column sm="3" className="text-end">
              {t('AllergyMaster.name')}
            </Form.Label>
            <Col sm="9">
              <Form.Control
                {...register('allergy_name')}
                type="text"
                placeholder=""
                isInvalid={!!errors.allergy_name}
              />
              <Form.Control.Feedback type="invalid">{errors.allergy_name?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="display_order">
            <Form.Label column sm="3" className="text-end">
              {t('AllergyMaster.display_order')}
            </Form.Label>
            <Col sm="9">
              <Form.Control
                {...register('display_order')}
                style={{ width: '80px' }}
                className="text-end"
                type="number"
                placeholder=""
                defaultValue={nextDisplayOrder}
                min={1}
                isInvalid={!!errors.display_order}
              />
              <Form.Control.Feedback type="invalid">{errors.display_order?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="display_flg">
            <Form.Label column sm="3" className="text-end">
              {t('AllergyMaster.display_flg')}
            </Form.Label>
            <Col sm="9">
              <Form.Check
                {...register('display_flg')}
                className="anra-checkbox"
                defaultChecked
                label=""
                type="checkbox"
              />
            </Col>
          </Form.Group>
          <div className="text-center mt-5">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="d-inline-flex gap-1 justify-content-center btn btn-primary align-items-center"
            >
              <span>{t('AllergyMaster.add')}</span>
              {isSubmitting && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default AllergyMasterForm;
